/**
 * Waypoints-menu entries (tour waypoints, then extra waypoints with `showInMenu`), Prev / Next
 * stepping and extra waypoints (named camera views off the tour path).
 * Pure helpers shared by createViewer, viewerUI, sceneToConfig and the tests.
 */
import * as pc from 'playcanvas';
import type { ExtraWaypointData, WaypointData } from '../types';

/** One entry of the viewer's 2D waypoints menu: the tour index it jumps to and its label. */
export interface WaypointMenuEntry {
    index: number;
    name: string;
}

/**
 * The waypoints the 2D waypoints menu lists, with their tour index. A waypoint with `hideInMenu`
 * is left out; the tour still visits it, so indices are never renumbered.
 */
export function waypointMenuEntries(waypoints: readonly WaypointData[] | undefined): WaypointMenuEntry[] {
    if (!Array.isArray(waypoints)) return [];
    const entries: WaypointMenuEntry[] = [];
    waypoints.forEach((wp, index) => {
        if (wp?.hideInMenu === true) return;
        entries.push({ index, name: wp?.name || `Waypoint ${index + 1}` });
    });
    return entries;
}

/** An extra waypoint the 2D waypoints menu lists after the tour waypoints. */
export interface ExtraWaypointMenuEntry {
    id: string;
    name: string;
}

/**
 * The extra waypoints the waypoints menu lists (those with `showInMenu`), in their order, after
 * the tour waypoints. Picking one flies there like goToExtraWaypoint. An unnamed one is "View n"
 * (its place in the extra waypoints list); the first of a repeated id wins.
 */
export function extraWaypointMenuEntries(list: readonly (Pick<ExtraWaypointData, 'id' | 'name' | 'showInMenu'> | null | undefined)[] | undefined): ExtraWaypointMenuEntry[] {
    if (!Array.isArray(list)) return [];
    const seen = new Set<string>();
    const entries: ExtraWaypointMenuEntry[] = [];
    list.forEach((wp, index) => {
        if (!wp || wp.showInMenu !== true || typeof wp.id !== 'string' || !wp.id || seen.has(wp.id)) return;
        seen.add(wp.id);
        entries.push({ id: wp.id, name: (typeof wp.name === 'string' && wp.name.trim()) || `View ${index + 1}` });
    });
    return entries;
}

/**
 * The tour index Prev / Next go to from `from`: the nearest waypoint in `direction` that Prev / Next
 * stop at (not `skipOnNextPrev`), wrapping around in a loop tour. Every waypoint counts as a stop
 * when they all skip. At the end of an open tour (no stop further on) it is the last stop, or the
 * first going back, when that is still ahead of `from` or at it; otherwise null: stay put. With no
 * skipped waypoints this is from ± 1, clamped or wrapped as before.
 */
export function waypointStepTarget(
    waypoints: readonly (Pick<WaypointData, 'skipOnNextPrev'> | null | undefined)[] | undefined,
    from: number,
    direction: 1 | -1,
    loop: boolean,
): number | null {
    const count = Array.isArray(waypoints) ? waypoints.length : 0;
    if (count === 0) return null;
    let stops: number[] = [];
    for (let i = 0; i < count; i++) if (waypoints![i]?.skipOnNextPrev !== true) stops.push(i);
    if (stops.length === 0) stops = Array.from({ length: count }, (_, i) => i);
    if (direction === 1) {
        const ahead = stops.find((i) => i > from);
        if (ahead !== undefined) return ahead;
        if (loop) return stops[0];
        const last = stops[stops.length - 1];
        return last >= from ? last : null;
    }
    let behind: number | undefined;
    for (const i of stops) if (i < from) behind = i;
    if (behind !== undefined) return behind;
    if (loop) return stops[stops.length - 1];
    return stops[0] <= from ? stops[0] : null;
}

type RotationLike = { x?: number; y?: number; z?: number; w?: number; _x?: number; _y?: number; _z?: number; _w?: number };

/**
 * A waypoint rotation as the PlayCanvas camera rotation: the editor's quaternion (Babylon
 * handedness, `x..w` or serialized `_x.._w`) with X and Y negated, or Euler degrees. The tour uses
 * the same conversion for its waypoints.
 */
export function waypointRotationToPlayCanvas(rot: RotationLike): pc.Quat {
    if ('_w' in rot || 'w' in rot) {
        const qx = rot._x ?? rot.x ?? 0;
        const qy = rot._y ?? rot.y ?? 0;
        const qz = rot._z ?? rot.z ?? 0;
        const qw = rot._w ?? rot.w ?? 1;
        return new pc.Quat(-qx, -qy, qz, qw);
    }
    if ('x' in rot && 'y' in rot && 'z' in rot) {
        const result = new pc.Quat();
        result.setFromEulerAngles(rot.x || 0, rot.y || 0, rot.z || 0);
        return result;
    }
    return new pc.Quat();
}

const finiteVec = (v: { x?: unknown; y?: unknown; z?: unknown } | null | undefined): v is { x: number; y: number; z: number } =>
    !!v && typeof v === 'object' && [v.x, v.y, v.z].every((n) => typeof n === 'number' && Number.isFinite(n));

/** FOV in degrees (radians below 3.5 are converted, as for tour waypoints), or undefined. */
export function waypointFovDegrees(fov: unknown): number | undefined {
    if (typeof fov !== 'number' || !Number.isFinite(fov) || fov <= 0) return undefined;
    return fov < 3.5 ? fov * (180 / Math.PI) : fov;
}

/**
 * The scene's extra waypoints, cleaned: entries without a string id or a finite position are
 * dropped, and the FOV is in degrees. Everything else passes through.
 */
export function normalizeExtraWaypoints(list: unknown): ExtraWaypointData[] {
    if (!Array.isArray(list)) return [];
    const out: ExtraWaypointData[] = [];
    for (const item of list as ExtraWaypointData[]) {
        if (!item || typeof item !== 'object' || typeof item.id !== 'string' || !item.id) continue;
        const position = item.position ?? { x: item.x, y: item.y, z: item.z };
        if (!finiteVec(position)) continue;
        const fov = waypointFovDegrees(item.fov);
        out.push({ ...item, fov });
    }
    return out;
}

/** The extra waypoint with this id, or null (none, or deleted). */
export function findExtraWaypoint(list: readonly ExtraWaypointData[] | undefined, id: unknown): ExtraWaypointData | null {
    if (typeof id !== 'string' || !id || !Array.isArray(list)) return null;
    return list.find((wp) => wp?.id === id) ?? null;
}

/** How far ahead of a first-person extra waypoint its look-at point (and later orbit pivot) sits. */
export const EXTRA_WAYPOINT_LOOK_DISTANCE = 2;

/** Where an extra waypoint puts the camera, in PlayCanvas world coordinates. */
export interface ExtraWaypointView {
    /** Camera position. */
    position: pc.Vec3;
    /** The point the camera faces on arrival; the orbit pivot when `orbit`. */
    lookAt: pc.Vec3;
    /** Orbit `lookAt` after arriving; otherwise look around from `position` (first person). */
    orbit: boolean;
    /** Vertical FOV in degrees, when the extra waypoint sets one. */
    fov?: number;
}

/**
 * The camera view of an extra waypoint, or null when its position is unusable. With an orbit
 * target (and no 'first-person' camera mode) the camera faces and later orbits that target;
 * otherwise it faces along its own rotation, `lookDistance` ahead.
 */
export function extraWaypointView(wp: ExtraWaypointData, lookDistance = EXTRA_WAYPOINT_LOOK_DISTANCE): ExtraWaypointView | null {
    const source = wp.position ?? { x: wp.x, y: wp.y, z: wp.z };
    if (!finiteVec(source)) return null;
    const position = new pc.Vec3(source.x, source.y, -source.z);
    let lookAt: pc.Vec3 | null = null;
    if (wp.cameraMode !== 'first-person' && finiteVec(wp.orbitTarget)) {
        const target = new pc.Vec3(wp.orbitTarget.x, wp.orbitTarget.y, -wp.orbitTarget.z);
        if (target.distance(position) >= 1e-4) lookAt = target;
    }
    const orbit = lookAt !== null;
    if (!lookAt) {
        const rotation = wp.rotation ? waypointRotationToPlayCanvas(wp.rotation) : new pc.Quat();
        const forward = rotation.transformVector(new pc.Vec3(0, 0, -1), new pc.Vec3());
        const distance = Number.isFinite(lookDistance) && lookDistance > 0 ? lookDistance : EXTRA_WAYPOINT_LOOK_DISTANCE;
        lookAt = position.clone().add(forward.mulScalar(distance));
    }
    return { position, lookAt, orbit, fov: waypointFovDegrees(wp.fov) };
}

/**
 * Which explore sub-mode an extra waypoint view uses: orbit for an orbit view, fly for a
 * first-person one, falling back to the other when the scene turned that one off. Null when the
 * scene has neither.
 */
export function extraWaypointExploreMode(view: Pick<ExtraWaypointView, 'orbit'>, isEnabled: (mode: 'orbit' | 'fly') => boolean): 'orbit' | 'fly' | null {
    const order: Array<'orbit' | 'fly'> = view.orbit ? ['orbit', 'fly'] : ['fly', 'orbit'];
    return order.find((mode) => isEnabled(mode)) ?? null;
}
